import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { FiPlay, FiSave, FiEdit2, FiTrash2, FiX, FiTrendingUp, FiCheckCircle, FiSearch, FiEye } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import usePagePermissions from '../../hooks/usePagePermissions';
import PageLayout from '../../components/PageLayout';
import {
  ContentCard, StatsRow, StatCard, StatIcon, StatInfo, StatValue, StatLabel,
  Table, Th, Tr, Td, SaveButton, FormGroup, Label, Input,
} from '../../components/SharedStyles';
import { bookValueOf } from '../../utils/depreciationHelper';
import {
  loadFixedAssets, loadAppreciations, postAppreciation, updateAppreciation, deleteAppreciation,
} from '../../utils/fixedAssetHelper';

// Asset Appreciation (upward revaluation). Works like the Depreciation Run:
// pick a date, load the assets, type the new value / % / amount per asset, post.
// Posts: Dr Fixed Asset account / Cr Revaluation Surplus (equity).

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const todayStr = () => new Date().toISOString().split('T')[0];
const fmt = (n) => {
  const v = round2(n);
  return v ? String(v) : '';
};
const methodLabel = (m) => (m === 'reducing_balance' ? 'WDV' : m === 'straight_line' ? 'SLM' : 'None');

// Row helpers — one of %, amount or new value is typed; the other two follow.
const fromAmount = (r, amount, raw) => {
  const amt = Math.max(0, round2(amount));
  return {
    ...r,
    amount: amt,
    amountText: raw != null ? raw : fmt(amt),
    pctText: r.bookValue > 0 && amt > 0 ? fmt((amt / r.bookValue) * 100) : '',
    valueText: amt > 0 ? fmt(r.bookValue + amt) : '',
    below: false,
    include: amt > 0 ? true : r.include,
  };
};

const AssetAppreciation = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit, canDelete } = usePagePermissions('fixedAssets', 'appreciation');
  const { formatCurrency } = useCurrency();
  const location = useLocation();

  const defaultDate = useCallback(() => {
    const t = todayStr();
    const fy = activeFinancialYear;
    if (fy?.endDate && t > fy.endDate) return fy.endDate;
    if (fy?.startDate && t < fy.startDate) return fy.startDate;
    return t;
  }, [activeFinancialYear]);

  const [docDate, setDocDate] = useState(todayStr());
  const [remarks, setRemarks] = useState('');
  const [rows, setRows] = useState(null); // null = not loaded yet
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [posting, setPosting] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [editing, setEditing] = useState(null); // voucher open for edit / view
  const [search, setSearch] = useState('');
  const [fillPct, setFillPct] = useState('');

  useEffect(() => { setDocDate(defaultDate()); }, [defaultDate]);

  const loadVouchers = useCallback(async () => {
    if (!activeCompany?.id) return [];
    try {
      const list = await loadAppreciations(activeCompany.id);
      setVouchers(list);
      return list;
    } catch (e) {
      console.error('Load appreciations failed:', e);
      return [];
    }
  }, [activeCompany]);
  useEffect(() => { loadVouchers(); }, [loadVouchers]);

  const readOnly = !!editing && !canEdit;

  const handleLoad = async () => {
    setEditing(null);
    setLoadingAssets(true);
    setRows(null);
    try {
      const assets = await loadFixedAssets(activeCompany.id);
      const list = assets
        .filter(a => a.status === 'Active' || a.status === 'FullyDepreciated')
        .map(a => ({
          assetId: a.id, assetCode: a.assetCode, assetName: a.assetName, categoryName: a.categoryName || '',
          method: a.depreciationMethod, bookValue: bookValueOf(a),
          include: false, amount: 0, amountText: '', pctText: '', valueText: '', below: false,
        }));
      setRows(list);
    } catch (e) {
      console.error('Load assets failed:', e);
      alert(`Failed to load assets: ${e.message}`);
    } finally {
      setLoadingAssets(false);
    }
  };

  const openVoucher = useCallback((v) => {
    const list = (v.appreciationSnapshot || []).filter(s => s.assetId).map(s => {
      const bookValue = round2(s.bookValueBefore);
      return fromAmount({
        assetId: s.assetId, assetCode: s.assetCode, assetName: s.assetName, categoryName: s.categoryName || '',
        method: null, bookValue, include: true, amount: 0, amountText: '', pctText: '', valueText: '', below: false,
      }, s.amount);
    });
    setEditing(v);
    setDocDate(v.docDate || defaultDate());
    setRemarks(v.remarks || '');
    setSearch('');
    setRows(list);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [defaultDate]);

  // Opened from a ledger / journal link (once per link).
  const deepLinkDone = useRef(null);
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId || deepLinkDone.current === targetId || !vouchers.length) return;
    const v = vouchers.find(x => x.id === targetId);
    if (v) {
      deepLinkDone.current = targetId;
      openVoucher(v);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, vouchers, openVoucher]);

  const closeVoucher = () => {
    setEditing(null);
    setRows(null);
    setRemarks('');
    setDocDate(defaultDate());
  };

  const updateRow = (assetId, fn) => setRows(prev => prev.map(r => (r.assetId === assetId ? fn(r) : r)));

  const onAmount = (assetId, text) => updateRow(assetId, r => fromAmount(r, Number(text) || 0, text));
  const onPct = (assetId, text) => updateRow(assetId, r => {
    const next = fromAmount(r, r.bookValue * ((Number(text) || 0) / 100));
    return { ...next, pctText: text };
  });
  const onValue = (assetId, text) => updateRow(assetId, r => {
    const v = Number(text) || 0;
    if (text !== '' && v < r.bookValue) {
      return { ...r, amount: 0, amountText: '', pctText: '', valueText: text, below: true };
    }
    const next = fromAmount(r, v - r.bookValue);
    return { ...next, valueText: text };
  });
  const toggleInclude = (assetId) => updateRow(assetId, r => ({ ...r, include: !r.include }));

  const visibleRows = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => `${r.assetCode} ${r.assetName} ${r.categoryName}`.toLowerCase().includes(q));
  }, [rows, search]);

  const applyPctToListed = () => {
    const pct = Number(fillPct);
    if (!(pct > 0)) { alert('Enter a percentage greater than zero.'); return; }
    const ids = new Set(visibleRows.map(r => r.assetId));
    setRows(prev => prev.map(r => {
      if (!ids.has(r.assetId)) return r;
      const next = fromAmount(r, r.bookValue * (pct / 100));
      return { ...next, pctText: String(pct), include: next.amount > 0 };
    }));
  };

  const included = (rows || []).filter(r => r.include && r.amount > 0);
  const total = round2(included.reduce((s, r) => s + r.amount, 0));

  const handleSave = async () => {
    if (!canEdit) { alert('You do not have permission to post appreciation.'); return; }
    if (!docDate) { alert('Select the appreciation date.'); return; }
    if (!editing && included.length === 0) { alert('Enter the appreciation for at least one asset.'); return; }
    if (editing && total <= 0) { alert('All amounts are zero — delete the voucher instead.'); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year.'); return; }
    const below = (rows || []).filter(r => r.include && r.below);
    if (below.length) { alert(`New value is below the book value for ${below.map(r => r.assetCode).join(', ')}. Fix it or untick the asset.`); return; }
    const msg = editing
      ? `Save changes to ${editing.docNumber}? New total ${formatCurrency(total)}.`
      : `Post appreciation of ${formatCurrency(total)} on ${included.length} asset(s), dated ${docDate}?`;
    if (!window.confirm(msg)) return;

    setPosting(true);
    try {
      if (editing) {
        await updateAppreciation(activeCompany.id, editing, {
          rows: rows.map(r => ({ assetId: r.assetId, amount: r.include ? r.amount : 0 })),
          docDate,
          remarks: remarks.trim(),
          userId: currentUser.uid,
        });
        alert(`Updated ${editing.docNumber}.`);
      } else {
        const posted = await postAppreciation(activeCompany.id, {
          rows: included.map(r => ({ assetId: r.assetId, amount: r.amount })),
          docDate,
          remarks: remarks.trim(),
          financialYearId: activeFinancialYear.id,
          branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
          branchName: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.branchName : null,
          userId: currentUser.uid,
        });
        alert(`Posted ${posted.docNumber} — appreciation of ${formatCurrency(posted.total)}.`);
      }
      closeVoucher();
      await loadVouchers();
    } catch (e) {
      console.error('Appreciation save failed:', e);
      alert(`${editing ? 'Failed to update' : 'Failed to post'}: ${e.message}`);
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (v) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete appreciation ${v.docNumber} (${formatCurrency(v.totalDebit || 0)})?\n\nIts accounting entry is removed and the appreciation is taken off each asset.`)) return;
    try {
      await deleteAppreciation(activeCompany.id, v, currentUser.uid);
      if (editing?.id === v.id) closeVoucher();
      await loadVouchers();
      alert(`Deleted ${v.docNumber}.`);
    } catch (e) {
      alert(`Could not delete: ${e.message}`);
    }
  };

  return (
    <PageLayout pageTitle="Asset Appreciation" breadcrumb="Fixed Assets / Appreciation">
      <ContentCard>
        <TopRow>
          <FormGroup style={{ minWidth: 160, marginBottom: 0 }}>
            <Label>Appreciation Date</Label>
            <Input type="date" value={docDate} disabled={readOnly} onChange={(e) => setDocDate(e.target.value)} />
          </FormGroup>
          <FormGroup style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
            <Label>Remarks (e.g. valuer / report no.)</Label>
            <Input type="text" value={remarks} disabled={readOnly} maxLength={200} placeholder="Optional" onChange={(e) => setRemarks(e.target.value)} />
          </FormGroup>
          {!editing && canEdit && (
            <LoadBtn type="button" onClick={handleLoad} disabled={loadingAssets}>
              <FiPlay /> {loadingAssets ? 'Loading...' : 'Load Assets'}
            </LoadBtn>
          )}
        </TopRow>
      </ContentCard>

      {editing && (
        <Banner>
          {readOnly ? <FiEye /> : <FiEdit2 />} {readOnly ? 'Viewing' : 'Editing'} <strong>&nbsp;{editing.docNumber}&nbsp;</strong>
          {readOnly ? '' : '— change amounts or untick assets, then Save Changes.'}
          <SmallBtn type="button" onClick={closeVoucher} style={{ marginLeft: 'auto' }}><FiX /> Close</SmallBtn>
        </Banner>
      )}

      {rows !== null && (
        <>
          <StatsRow>
            <StatCard>
              <StatIcon $color={colors.primary.main}><FiCheckCircle /></StatIcon>
              <StatInfo><StatValue>{included.length}</StatValue><StatLabel>Assets Selected</StatLabel></StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon $color={colors.success.main}><FiTrendingUp /></StatIcon>
              <StatInfo><StatValue>{formatCurrency(total)}</StatValue><StatLabel>Total Appreciation</StatLabel></StatInfo>
            </StatCard>
          </StatsRow>

          <ContentCard>
            {rows.length === 0 ? (
              <EmptyMsg>No active assets. Add assets in the Asset Register first.</EmptyMsg>
            ) : (
              <>
                {!editing && (
                  <ToolRow>
                    <SearchWrap>
                      <FiSearch />
                      <input placeholder="Search code, asset, category..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </SearchWrap>
                    <FillWrap>
                      <span>Appreciation %</span>
                      <input type="number" min="0" step="0.01" value={fillPct} onChange={(e) => setFillPct(e.target.value)} placeholder="e.g. 10" />
                      <SmallBtn type="button" onClick={applyPctToListed}>Apply to listed assets</SmallBtn>
                    </FillWrap>
                  </ToolRow>
                )}
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Include</Th>
                        <Th>Code</Th>
                        <Th>Asset</Th>
                        <Th>Category</Th>
                        {!editing && <Th>Method</Th>}
                        <Th style={{ textAlign: 'right' }}>{editing ? 'Book Value (before)' : 'Book Value'}</Th>
                        <Th style={{ textAlign: 'right' }}>%</Th>
                        <Th style={{ textAlign: 'right' }}>Appreciation</Th>
                        <Th style={{ textAlign: 'right' }}>New Value</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map(r => (
                        <Tr key={r.assetId} style={{ opacity: r.include ? 1 : 0.6 }}>
                          <Td><input type="checkbox" checked={r.include} disabled={readOnly} onChange={() => toggleInclude(r.assetId)} /></Td>
                          <Td><strong>{r.assetCode}</strong></Td>
                          <Td>{r.assetName}</Td>
                          <Td>{r.categoryName}</Td>
                          {!editing && <Td>{methodLabel(r.method)}</Td>}
                          <Td style={{ textAlign: 'right' }}>{formatCurrency(r.bookValue)}</Td>
                          <Td style={{ textAlign: 'right' }}>
                            <NumInput $w={80} type="number" min="0" step="0.01" value={r.pctText} disabled={readOnly}
                              aria-label={`Appreciation % ${r.assetCode}`} onChange={(e) => onPct(r.assetId, e.target.value)} />
                          </Td>
                          <Td style={{ textAlign: 'right' }}>
                            <NumInput type="number" min="0" step="0.01" value={r.amountText} disabled={readOnly}
                              aria-label={`Appreciation ${r.assetCode}`} onChange={(e) => onAmount(r.assetId, e.target.value)} />
                          </Td>
                          <Td style={{ textAlign: 'right' }}>
                            <NumInput type="number" min="0" step="0.01" value={r.valueText} disabled={readOnly} $error={r.below}
                              aria-label={`New value ${r.assetCode}`} onChange={(e) => onValue(r.assetId, e.target.value)} />
                            {r.below && <BelowNote>Below book value — increases only</BelowNote>}
                          </Td>
                        </Tr>
                      ))}
                      {visibleRows.length === 0 && (
                        <tr><Td colSpan={editing ? 8 : 9}><EmptyMsg style={{ padding: 20 }}>No assets match your search.</EmptyMsg></Td></tr>
                      )}
                    </tbody>
                  </Table>
                </TableScroll>

                <PostRow>
                  <span>Posts: <strong>Dr Fixed Asset account</strong> / <strong>Cr Revaluation Surplus</strong> (equity) {formatCurrency(total)}. The original cost of each asset stays the same.</span>
                  {canEdit && (
                    <SaveButton $approve type="button" onClick={handleSave} disabled={posting || total <= 0}>
                      <FiSave /> {posting ? 'Saving...' : (editing ? `Save Changes (${formatCurrency(total)})` : `Post ${formatCurrency(total)}`)}
                    </SaveButton>
                  )}
                </PostRow>
              </>
            )}
          </ContentCard>
        </>
      )}

      <ContentCard style={{ marginTop: 16 }}>
        <h4 style={{ margin: '0 0 12px', color: colors.text.primary }}>Posted Appreciations</h4>
        {vouchers.length === 0 ? (
          <EmptyMsg>No appreciation posted yet.</EmptyMsg>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Voucher</Th>
                  <Th>Date</Th>
                  <Th style={{ textAlign: 'right' }}>Assets</Th>
                  <Th style={{ textAlign: 'right' }}>Amount</Th>
                  <Th>Remarks</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map(v => (
                  <Tr key={v.id} style={editing?.id === v.id ? { background: `${colors.primary.main}12` } : undefined}>
                    <Td><strong>{v.docNumber}</strong></Td>
                    <Td>{v.docDate}</Td>
                    <Td style={{ textAlign: 'right' }}>{(v.appreciationSnapshot || []).length}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatCurrency(v.totalDebit || 0)}</Td>
                    <Td>{v.remarks || '—'}</Td>
                    <Td>
                      <SmallBtn type="button" onClick={() => openVoucher(v)} title={canEdit ? 'Edit' : 'View'}>{canEdit ? <FiEdit2 /> : <FiEye />}</SmallBtn>
                      {canDelete && <SmallBtn type="button" $danger onClick={() => handleDelete(v)} title="Delete" style={{ marginLeft: 6 }}><FiTrash2 /></SmallBtn>}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </ContentCard>
    </PageLayout>
  );
};

const TopRow = styled.div`display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap;`;
const LoadBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px; padding: 10px 18px; height: 40px;
  background: ${colors.primary.main}; color: #fff; border: none; border-radius: 8px; cursor: pointer; font-size: 14px;
  &:disabled { opacity: 0.6; cursor: default; }
`;
const SmallBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px; padding: 6px 8px; font-size: 13px;
  background: none; border: 1px solid ${p => p.$danger ? colors.error.main : colors.border.light}; border-radius: 6px;
  color: ${p => p.$danger ? colors.error.main : colors.text.secondary}; cursor: pointer; white-space: nowrap;
  &:hover { border-color: ${p => p.$danger ? colors.error.dark : colors.primary.main}; color: ${p => p.$danger ? colors.error.dark : colors.primary.main}; }
`;
const Banner = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 12px 16px; margin-bottom: 16px; flex-wrap: wrap;
  background: ${colors.warning.main}1a; border: 1px solid ${colors.warning.main}; border-radius: 8px;
  color: ${colors.warning.dark}; font-size: 14px;
`;
const EmptyMsg = styled.div`padding: 40px 20px; text-align: center; color: ${colors.text.secondary};`;
const ToolRow = styled.div`display: flex; gap: 12px; align-items: center; justify-content: space-between; flex-wrap: wrap; margin-bottom: 12px;`;
const SearchWrap = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 0 10px; height: 36px; min-width: 240px; flex: 1; max-width: 380px;
  border: 1px solid ${colors.border.light}; border-radius: 8px; color: ${colors.text.secondary};
  input { border: none; outline: none; flex: 1; min-width: 0; font-size: 13px; background: transparent; color: ${colors.text.primary}; }
`;
const FillWrap = styled.div`
  display: flex; align-items: center; gap: 8px; font-size: 13px; color: ${colors.text.secondary}; flex-wrap: wrap;
  input { width: 90px; height: 32px; padding: 0 8px; border: 1px solid ${colors.border.light}; border-radius: 6px; text-align: right; font-size: 13px; }
`;
const TableScroll = styled.div`width: 100%; overflow-x: auto;`;
const NumInput = styled.input`
  width: ${p => p.$w || 120}px; text-align: right; padding: 6px 8px; border-radius: 6px; font-size: 13px;
  border: 1px solid ${p => p.$error ? colors.error.main : colors.border.light};
  background: ${colors.background.main}; color: ${colors.text.primary};
  &:disabled { background: ${colors.background.paper}; }
`;
const BelowNote = styled.div`font-size: 11px; color: ${colors.error.main}; margin-top: 4px; white-space: normal; max-width: 140px; margin-left: auto;`;
const PostRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 20px;
  padding-top: 16px; border-top: 1px solid ${colors.border.light}; font-size: 13px; color: ${colors.text.secondary};
  flex-wrap: wrap;
`;

export default AssetAppreciation;
